import React, { useState, useEffect } from 'react';
import { CreatePOIRequest, NearbyGooglePlace, poiService } from '../services/poiService';
import { getAllCategories, getCategoryIdFromReadable } from '../utils/categoryUtils';
import { geocodingService } from '../services/geocodingService';

interface POIFormProps {
  location: { lat: number; lng: number };
  onSubmit: (data: CreatePOIRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const POIForm: React.FC<POIFormProps> = ({ location, onSubmit, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    latitude: location.lat,
    longitude: location.lng,
    category: '',
    city: '',
    country: '',
    google_place_id: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categories] = useState(() => getAllCategories());
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isSearchingPlaces, setIsSearchingPlaces] = useState(false);
  const [showPlacesModal, setShowPlacesModal] = useState(false);
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyGooglePlace[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<NearbyGooglePlace | null>(null);

  // Perform reverse geocoding on mount to get city and country
  useEffect(() => {
    const fetchLocationInfo = async () => {
      try {
        setIsLoadingLocation(true);
        const result = await geocodingService.reverseGeocode(location.lat, location.lng);
        setFormData(prev => ({
          ...prev,
          city: result.city,
          country: result.country
        }));
      } catch (error) {
        console.error('Error fetching location info:', error);
      } finally {
        setIsLoadingLocation(false);
      }
    };

    fetchLocationInfo();
  }, [location.lat, location.lng]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'POI name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'POI name must be less than 255 characters';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    } else if (formData.category.length > 255) {
      newErrors.category = 'Category must be less than 255 characters';
    }
    
    if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    
    if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Convert human-readable category to Google Places API identifier
      const submissionData = {
        ...formData,
        category: getCategoryIdFromReadable(formData.category)
      };
      await onSubmit(submissionData);
    } catch (error) {
      console.error('Error submitting POI form:', error);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLinkGooglePlace = async () => {
    try {
      setIsSearchingPlaces(true);
      const response = await poiService.searchNearbyGooglePlaces(
        formData.latitude,
        formData.longitude,
        20 // Search within 100 meters
      );
      
      if (!response.metadata.configured) {
        alert('Google Places API is not configured on the server');
        return;
      }
      
      setNearbyPlaces(response.data);
      setShowPlacesModal(true);
    } catch (error) {
      console.error('Error searching Google Places:', error);
      alert('Failed to search for nearby places. Please try again.');
    } finally {
      setIsSearchingPlaces(false);
    }
  };

  const handleSelectPlace = (place: NearbyGooglePlace) => {
    setSelectedPlace(place);
    setFormData(prev => ({
      ...prev,
      name: place.name,
      google_place_id: place.placeId
    }));
    setShowPlacesModal(false);
  };

  const handleClearPlace = () => {
    setSelectedPlace(null);
    setFormData(prev => ({
      ...prev,
      google_place_id: ''
    }));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Point of Interest</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Google Place Link Section */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                Link to Google Maps Place
              </h4>
              {selectedPlace ? (
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    Linked to: {selectedPlace.name}
                  </span>
                  <button
                    type="button"
                    onClick={handleClearPlace}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Search for a Google Place at these coordinates to auto-fill details
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleLinkGooglePlace}
              disabled={isLoading || isSearchingPlaces}
              className="btn-secondary text-sm py-2 px-3"
            >
              {isSearchingPlaces ? (
                <>
                  <div 
                    className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"
                    role="status"
                    aria-live="polite"
                  >
                    <span className="sr-only">Searching for places...</span>
                  </div>
                  Searching...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search Places
                </>
              )}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="poi-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            POI Name *
          </label>
          <input
            id="poi-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`form-input ${errors.name ? 'border-red-500' : ''}`}
            placeholder="Enter the name of this location"
            disabled={isLoading}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="poi-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category *
          </label>
          <select
            id="poi-category"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className={`form-input ${errors.category ? 'border-red-500' : ''}`}
            disabled={isLoading}
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
          {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="poi-city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              City
            </label>
            <input
              id="poi-city"
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="form-input"
              placeholder={isLoadingLocation ? "Loading..." : "Enter city name"}
              disabled={isLoading || isLoadingLocation}
            />
          </div>

          <div>
            <label htmlFor="poi-country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Country
            </label>
            <input
              id="poi-country"
              type="text"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="form-input"
              placeholder={isLoadingLocation ? "Loading..." : "Enter country name"}
              disabled={isLoading || isLoadingLocation}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="poi-latitude" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Latitude
            </label>
            <input
              id="poi-latitude"
              type="number"
              step="any"
              value={formData.latitude}
              onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value))}
              className={`form-input ${errors.latitude ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.latitude && <p className="text-red-500 text-sm mt-1">{errors.latitude}</p>}
          </div>

          <div>
            <label htmlFor="poi-longitude" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Longitude
            </label>
            <input
              id="poi-longitude"
              type="number"
              step="any"
              value={formData.longitude}
              onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value))}
              className={`form-input ${errors.longitude ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.longitude && <p className="text-red-500 text-sm mt-1">{errors.longitude}</p>}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              'Create POI'
            )}
          </button>
        </div>
      </form>

      {/* Places Selection Modal */}
      {showPlacesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Select a Google Place
                </h3>
                <button
                  onClick={() => setShowPlacesModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-8rem)]">
              {nearbyPlaces.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400">No places found at this location</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    Try adjusting the coordinates or create the POI without linking
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {nearbyPlaces.map((place) => (
                    <button
                      key={place.placeId}
                      onClick={() => handleSelectPlace(place)}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                            {place.name}
                          </h4>
                          {place.vicinity && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              {place.vicinity}
                            </p>
                          )}
                          {place.types.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {place.types.slice(0, 3).map((type) => (
                                <span
                                  key={type}
                                  className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                                >
                                  {type.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowPlacesModal(false)}
                className="w-full btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POIForm;